"use client";

import React, { useState, useEffect, useCallback } from "react";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import styled from "styled-components";
import { Checkbox, Text } from "@lmn/design-system";
import IconBed from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconBed.js";
import IconPet from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-linear/IconPet.js";
import IconSwimmingpool from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-linear/IconSwimmingpool.js";
import MultiSelect from "@/components/UI/MultiSelect/MultiSelect";
import initialState from "@/lib/StaticData/searchFormData";
import { useFilterState } from "@/lib/Providers/FilterStateProvider/FilterStateProvider";
import ResponsiveTooltip from "@/components/UI/Tooltip/ResponsiveTooltip";
import IconCircleInfo from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-linear/IconCircleInfo.js";
import { HotelCriteriaIds } from "@/lib/Types/searchForm";

const roomTypeCategoryIcons = (icon: string) => {
  const iconMap: { [key: string]: React.ReactNode } = {
    IconPet: <IconPet size={24} aria-hidden="true" />,
    pool: <IconSwimmingpool size={24} aria-hidden="true" />,
    bed: <IconBed size={24} aria-hidden="true" />,
  };
  return iconMap[icon];
};

const FilterWrapper = styled.div<{ $expanded?: boolean }>`
  width: 100%;
  margin-top: 1rem;
  position: relative;
  ${({ $expanded }) =>
    $expanded &&
    `
    display: flex;
    flex-direction: column-reverse;
  `}
`;

const CheckboxesContainer = styled.div<{ $expanded?: boolean }>`
  position: relative;
  z-index: 0;
  ${({ $expanded }) =>
    $expanded &&
    `
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  `}
`;

const ExpandedRoomTypesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const CheckboxWrapper = styled.div`
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
`;

const ClickOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: pointer;
  z-index: 0;
`;

const Label = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 0.5rem;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  margin: 1rem 0;
  background-color: ${({ theme }) =>
    theme.kallisto.color.background.static.backgroundDisabled};
`;

interface RoomTypeFilterProps {
  delayedUpdate?: boolean;
  isHotelOnly?: boolean;
  expanded?: boolean;
}

const RoomTypeFilter: React.FC<RoomTypeFilterProps> = ({
  delayedUpdate = false,
  isHotelOnly = false,
  expanded = false,
}) => {
  const searchParams = useReactiveSearchParams();
  const { tempFilters, updateFilter, updateTempFilterArray, updateTempFilter } =
    useFilterState();
  const roomTypes = initialState.roomType;

  const [selectedRoomTypes, setSelectedRoomTypes] = useState<string[]>(() => {
    return delayedUpdate
      ? tempFilters.get("roomType")?.split(",") ?? []
      : searchParams.get("roomType")?.split(",") ?? [];
  });

  const [isOceanView, setIsOceanView] = useState(() => {
    return delayedUpdate
      ? tempFilters.get("oceanView") === "true"
      : searchParams.get("oceanView") === "true";
  });

  const [selectedCategories, setSelectedCategories] = useState<number[]>(() => {
    const categories = delayedUpdate
      ? tempFilters.get("categories")?.split(",") ?? []
      : searchParams.get("categories")?.split(",") ?? [];
    return categories
      .map(Number)
      .filter((id) =>
        initialState.roomTypeCategories.allIds.includes(id as HotelCriteriaIds)
      );
  });

  useEffect(() => {
    setSelectedRoomTypes(searchParams.get("roomType")?.split(",") ?? []);
    setIsOceanView(searchParams.get("oceanView") === "true");
    const categories = searchParams.get("categories")?.split(",") ?? [];
    const roomTypeCategories = categories
      .map(Number)
      .filter((id) =>
        initialState.roomTypeCategories.allIds.includes(id as HotelCriteriaIds)
      );
    setSelectedCategories(roomTypeCategories);
  }, [searchParams]);

  const handleRoomTypeChange = useCallback(
    (selected: string[]) => {
      setSelectedRoomTypes(selected);

      if (delayedUpdate) {
        updateTempFilterArray("roomType", selected);
      } else {
        updateFilter(
          "roomType",
          selected.length > 0 ? selected.join(",") : null
        );
      }
    },
    [updateFilter, updateTempFilterArray, delayedUpdate]
  );

  const handleOceanViewChange = useCallback(() => {
    const newValue = !isOceanView;
    setIsOceanView(newValue);

    if (delayedUpdate) {
      updateTempFilter("oceanView", newValue ? "true" : null);
    } else {
      updateFilter("oceanView", newValue ? "true" : null);
    }
  }, [updateFilter, updateTempFilter, isOceanView, delayedUpdate]);

  const handleCategoryChange = useCallback(
    (categoryId: HotelCriteriaIds) => {
      const isCurrentlyChecked = selectedCategories.includes(categoryId);
      const newSelectedCategories = isCurrentlyChecked
        ? selectedCategories.filter((id) => id !== categoryId)
        : [...selectedCategories, categoryId];

      setSelectedCategories(newSelectedCategories);

      // Get all current categories (including those not in roomTypeCategories)
      const allCurrentCategories = delayedUpdate
        ? tempFilters.get("categories")?.split(",") ?? []
        : searchParams.get("categories")?.split(",") ?? [];

      // Remove all room type categories and add the new selected ones
      const otherCategories = allCurrentCategories.filter(
        (id: string) =>
          !initialState.roomTypeCategories.allIds.includes(
            Number(id) as HotelCriteriaIds
          )
      );

      const newCategories = [
        ...otherCategories,
        ...newSelectedCategories.map(String),
      ];

      if (delayedUpdate) {
        updateTempFilterArray("categories", newCategories);
      } else {
        updateFilter(
          "categories",
          newCategories.length > 0 ? newCategories.join(",") : null
        );
      }
    },
    [
      selectedCategories,
      updateFilter,
      updateTempFilterArray,
      delayedUpdate,
      searchParams,
      tempFilters,
    ]
  );

  const options = roomTypes.allIds.map((id) => ({
    value: id.toString(),
    label: roomTypes.byId[id].label,
  }));

  const handleRoomTypeCheckboxChange = useCallback(
    (roomTypeId: string) => {
      const isCurrentlySelected = selectedRoomTypes.includes(roomTypeId);
      const newSelectedRoomTypes = isCurrentlySelected
        ? selectedRoomTypes.filter((id) => id !== roomTypeId)
        : [...selectedRoomTypes, roomTypeId];

      handleRoomTypeChange(newSelectedRoomTypes);
    },
    [selectedRoomTypes, handleRoomTypeChange]
  );

  return (
    <FilterWrapper data-testid="roomTypeFilter" $expanded={expanded}>
      {expanded ? (
        <ExpandedRoomTypesGrid>
          {options.map((option) => {
            const isChecked = selectedRoomTypes.includes(option.value);
            return (
              <CheckboxWrapper key={option.value}>
                {delayedUpdate && (
                  <ClickOverlay
                    onClick={() => handleRoomTypeCheckboxChange(option.value)}
                  />
                )}
                <Checkbox
                  checkbox={{
                    id: `roomType-${option.value}`,
                    label: option.label,
                  }}
                  isChecked={isChecked}
                  onChange={
                    delayedUpdate
                      ? () => { }
                      : () => handleRoomTypeCheckboxChange(option.value)
                  }
                  aria-describedby={`roomType-${option.value}-Filter`}
                />
              </CheckboxWrapper>
            );
          })}
        </ExpandedRoomTypesGrid>
      ) : (
        <MultiSelect
          options={options}
          value={selectedRoomTypes}
          onChange={handleRoomTypeChange}
          placeholder="beliebig"
          label="Zimmertyp"
          icon={<IconBed size={24} aria-hidden="true" />}
          aria-label="Select room types"
          dataTestId="roomTypeList"
        />
      )}

      {expanded && <Divider />}
      <CheckboxesContainer $expanded={expanded}>
        <CheckboxWrapper>
          {delayedUpdate && (
            <ClickOverlay onClick={() => handleOceanViewChange()} />
          )}
          <IconBed aria-hidden="true" size={24} />
          <Checkbox
            checkbox={{
              id: "oceanView",
              label: "Zimmer mit Meerblick",
            }}
            isChecked={isOceanView}
            onChange={delayedUpdate ? () => { } : () => handleOceanViewChange()}
            aria-describedby="oceanView-Filter"
          />
        </CheckboxWrapper>

        {initialState.roomTypeCategories.allIds
          .filter((id) => isHotelOnly || id !== 79)
          .map((id) => {
            const criteria = initialState.hotelCriteria.byId[id];
            const isChecked = selectedCategories.includes(id);

            // Special handling for Swim up Pool with tooltip
            const label =
              id === 81 ? (
                <Label>
                  <Text variant="BodySmall">{criteria.label}</Text>
                  <ResponsiveTooltip
                    content="Zimmer mit direktem Zugang zu einem privaten oder gemeinschaftlichen Pool."
                    trigger={<IconCircleInfo size={14} />}
                  />
                </Label>
              ) : (
                criteria.label
              );

            return (
              <CheckboxWrapper key={id}>
                {delayedUpdate && (
                  <ClickOverlay onClick={() => handleCategoryChange(id)} />
                )}
                {roomTypeCategoryIcons(criteria.icon)}
                <Checkbox
                  checkbox={{
                    id: id.toString(),
                    label,
                  }}
                  isChecked={isChecked}
                  onChange={
                    delayedUpdate ? () => { } : () => handleCategoryChange(id)
                  }
                  aria-describedby={`category-${criteria.label}-Filter`}
                />
              </CheckboxWrapper>
            );
          })}
      </CheckboxesContainer>
    </FilterWrapper>
  );
};

export default RoomTypeFilter;
